import { useContext, useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { Building2, Loader2, User } from "lucide-react";
import { PaymentContext } from "../../contexts/PaymentContext";
import { API_BASE } from "../../shared/Constants";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../../utils/auth";
import { InvoiceConfig, InvoicePFConfig, InvoicePJConfig } from "../../shared/Interfaces";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { cn } from "../../lib/utils";
import { reportHandledError, reportApiError } from "../../utils/reportError";

export interface Locations {
  [state: string]: { [city: string]: string };
}

const inputClass =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-ring focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60";

export default function InvoiceConfigModal() {
  const {
    setPaymentToast,
    invoiceConfig,
    invoiceType,
    billingScope,
    activeOrganizationId,
  } = useContext(PaymentContext);
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const buildBillingUrl = (path: string) => {
    const params = new URLSearchParams();
    if (billingScope === "organization" && activeOrganizationId) {
      params.set("organization_id", activeOrganizationId);
    }
    const query = params.toString();
    return `${API_BASE}/api/billing${path}${query ? `?${query}` : ""}`;
  };

  const [currentTab, setCurrentTab] = useState<"PF" | "PJ">(
    invoiceType === "PJ" ? "PJ" : "PF"
  );
  const [locations, setLocations] = useState<Locations>({});
  const [selectedState, setSelectedState] = useState(invoiceConfig ? invoiceConfig.state : "");
  const [selectedCity, setSelectedCity] = useState(invoiceConfig ? invoiceConfig.city : "");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (invoiceConfig && invoiceType) setCurrentTab(invoiceType as "PF" | "PJ");
  }, [invoiceConfig, invoiceType]);

  useEffect(() => {
    (async () => {
      try {
        const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!access_token) return;
        const response = await fetch(`${API_BASE}/api/states-cities`, {
          headers: { Authorization: `Auth ${access_token}` },
        });
        if (response.ok) setLocations(await response.json());
        else reportApiError(response, { feature: "invoice.fetch", method: "GET" });
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "invoice.fetch" });
        setPaymentToast({ message: "Erro ao carregar cidades e estados.", type: "error" });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!locations[selectedState]?.hasOwnProperty(selectedCity)) setSelectedCity("");
  }, [selectedState, selectedCity, locations]);

  const handleSave = async () => {
    if (saving) return;
    const isPj = currentTab === "PJ";

    const requiredIds = isPj
      ? ["cnpj", "email", "razao-social", "address", "cep", "address-number", "neighborhood"]
      : ["cpf", "email", "name", "address", "cep", "address-number", "neighborhood"];

    const data: any = {};
    let isValid = true;

    requiredIds.forEach((id) => {
      const input = document.getElementById(id) as HTMLInputElement;
      const value = input?.value.trim() || "";
      data[id === "razao-social" ? "razao_social" : id.replace("-", "_")] = value;
      if (!value) {
        if (input) input.style.borderColor = "rgb(239 68 68)";
        isValid = false;
      } else if (input) {
        input.style.borderColor = "";
      }
    });

    const emailInput = document.getElementById("email") as HTMLInputElement;
    const emailValue = emailInput?.value.trim() || "";
    data.email = emailValue;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailValue || !emailRegex.test(emailValue)) {
      if (emailInput) emailInput.style.borderColor = "rgb(239 68 68)";
      isValid = false;
    } else if (emailInput) {
      emailInput.style.borderColor = "";
    }

    const isValidCpf = !isPj && /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(data.cpf);
    const isValidCnpj = isPj && /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(data.cnpj);
    const isValidCep = /^\d{5}-\d{3}$/.test(data.cep);

    if (isPj && !isValidCnpj) {
      document.getElementById("cnpj")?.setAttribute("style", "border-color: rgb(239 68 68);");
      isValid = false;
    } else if (!isPj && !isValidCpf) {
      document.getElementById("cpf")?.setAttribute("style", "border-color: rgb(239 68 68);");
      isValid = false;
    }

    if (!isValidCep) {
      document.getElementById("cep")?.setAttribute("style", "border-color: rgb(239 68 68);");
      isValid = false;
    }

    if (!isValid || !selectedState || !selectedCity) {
      setPaymentToast({ message: "Preencha todos os campos corretamente.", type: "error" });
      return;
    }

    const complementInput = document.getElementById("address-complement") as HTMLInputElement;
    const complementValue = complementInput?.value.trim() || "";
    if (complementValue) data.address_complement = complementValue;

    const config: InvoiceConfig = {
      invoice_type: isPj ? "PJ" : "PF",
      invoice_config: { ...data, state: selectedState, city: selectedCity },
    };

    if (billingScope === "organization" && !activeOrganizationId) {
      setPaymentToast({
        message: "Selecione uma organização para salvar os dados fiscais.",
        type: "error",
      });
      return;
    }

    setSaving(true);
    const url = buildBillingUrl("/save-invoice-config");

    try {
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Auth ${access_token}`,
        },
        body: JSON.stringify(config),
      });

      if (response.ok) {
        setPaymentToast({ message: "Dados fiscais salvos com sucesso!", type: "success" });
      } else if (response.status === 400) {
        setPaymentToast({ message: (await response.json()).status, type: "error" });
      } else {
        reportApiError(response, { feature: "invoice.save", method: "POST" });
        setPaymentToast({ message: "Erro ao salvar dados fiscais.", type: "error" });
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "invoice.save" });
      setPaymentToast({ message: "Erro ao salvar dados fiscais.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const getCurrentData = (): InvoicePFConfig | InvoicePJConfig | null => {
    if (!invoiceConfig || !invoiceType) return null;
    if (currentTab === invoiceType) return invoiceConfig;
    return null;
  };

  const currentData = getCurrentData();
  const isPj = currentTab === "PJ";

  return (
    <div className="flex flex-col">
      <div className="px-6 pb-3 pt-4">
        <div className="mb-4">
          <h2 className="text-[15px] font-semibold tracking-tight text-foreground">
            Configurar dados fiscais
          </h2>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            Configure seus dados para emissão de notas fiscais
          </p>
        </div>

        <div className="mb-5 flex gap-2">
          <button
            type="button"
            onClick={() => setCurrentTab("PF")}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors",
              currentTab === "PF"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background text-muted-foreground hover:bg-muted"
            )}
          >
            <User className="h-4 w-4" />
            Pessoa Física
          </button>
          <button
            type="button"
            onClick={() => setCurrentTab("PJ")}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors",
              currentTab === "PJ"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background text-muted-foreground hover:bg-muted"
            )}
          >
            <Building2 className="h-4 w-4" />
            Pessoa Jurídica
          </button>
        </div>

        <div className="grid max-h-[55vh] grid-cols-1 gap-3 overflow-y-auto md:grid-cols-2">
          {isPj ? (
            <>
              <Field label="CNPJ *">
                <InputMask
                  id="cnpj"
                  mask="99.999.999/9999-99"
                  maskChar=""
                  placeholder="00.000.000/0000-00"
                  defaultValue={
                    isPj && currentData ? (currentData as InvoicePJConfig).cnpj : ""
                  }
                  className={inputClass}
                />
              </Field>
              <Field label="Razão Social *">
                <input
                  id="razao-social"
                  type="text"
                  placeholder="Nome da empresa"
                  defaultValue={
                    isPj && currentData ? (currentData as InvoicePJConfig).razao_social : ""
                  }
                  className={inputClass}
                />
              </Field>
            </>
          ) : (
            <>
              <Field label="CPF *">
                <InputMask
                  id="cpf"
                  mask="999.999.999-99"
                  maskChar=""
                  placeholder="000.000.000-00"
                  defaultValue={
                    !isPj && currentData ? (currentData as InvoicePFConfig).cpf : ""
                  }
                  className={inputClass}
                />
              </Field>
              <Field label="Nome completo *">
                <input
                  id="name"
                  type="text"
                  placeholder="Seu nome completo"
                  defaultValue={
                    !isPj && currentData ? (currentData as InvoicePFConfig).name : ""
                  }
                  className={inputClass}
                />
              </Field>
            </>
          )}

          <Field label="E-mail *">
            <input
              id="email"
              type="email"
              placeholder="seu@email.com"
              defaultValue={currentData?.email || ""}
              className={inputClass}
            />
          </Field>

          <Field label="CEP *">
            <InputMask
              id="cep"
              mask="99999-999"
              maskChar=""
              placeholder="00000-000"
              defaultValue={currentData?.cep || ""}
              className={inputClass}
            />
          </Field>

          <Field label="Estado *">
            {loading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <select
                id="state"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className={inputClass}
              >
                <option value="">Selecione o estado</option>
                {Object.keys(locations).map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Cidade *">
            {loading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <select
                id="city"
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className={inputClass}
              >
                <option value="">Selecione a cidade</option>
                {selectedState &&
                  Object.keys(locations[selectedState] || {}).map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
              </select>
            )}
          </Field>

          <Field label="Endereço *" className="md:col-span-2">
            <input
              id="address"
              type="text"
              placeholder="Rua, avenida, etc."
              defaultValue={currentData?.address || ""}
              className={inputClass}
            />
          </Field>

          <Field label="Número *">
            <input
              id="address-number"
              type="text"
              placeholder="123"
              defaultValue={currentData?.address_number || ""}
              className={inputClass}
            />
          </Field>

          <Field label="Bairro *">
            <input
              id="neighborhood"
              type="text"
              placeholder="Nome do bairro"
              defaultValue={currentData?.neighborhood || ""}
              className={inputClass}
            />
          </Field>

          <Field label="Complemento" className="md:col-span-2">
            <input
              id="address-complement"
              type="text"
              placeholder="Apartamento, bloco, etc. (opcional)"
              defaultValue={currentData?.address_complement || ""}
              className={inputClass}
            />
          </Field>
        </div>
      </div>

      <div className="border-t border-border bg-muted/30 px-6 py-4">
        <Button onClick={handleSave} disabled={saving} className="w-full gap-2">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {saving ? "Salvando..." : "Salvar dados fiscais"}
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}
